import React, { useState, useEffect } from 'react';
import { X, Upload, Mic, Play, Pause, Trash2, Save } from 'lucide-react';
import { voiceApi, Voice, VoiceSettings } from '../../services/audioApi';

interface EditVoiceModalProps {
  isOpen: boolean;
  voice: Voice;
  onClose: () => void;
  onVoiceUpdated: () => void;
}

const EditVoiceModal: React.FC<EditVoiceModalProps> = ({
  isOpen,
  voice,
  onClose,
  onVoiceUpdated,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    language: 'en',
    gender: 'neutral' as 'male' | 'female' | 'neutral',
    age: 'adult' as 'child' | 'young_adult' | 'adult' | 'senior',
    accent: 'neutral',
  });

  // Audio samples
  const [audioSamples, setAudioSamples] = useState<File[]>([]);
  const [existingSamples, setExistingSamples] = useState(voice.sampleFiles || []);

  // Voice settings
  const [voiceSettings, setVoiceSettings] = useState<VoiceSettings>({
    stability: 0.75,
    similarityBoost: 0.75,
    style: 0.0,
    speakerBoost: true,
  });

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);

  // Audio playback
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);

  // Initialize form data with existing voice data
  useEffect(() => {
    if (voice) {
      setFormData({
        name: voice.name || '',
        description: voice.description || '',
        language: voice.language || 'en',
        gender: voice.gender || 'neutral',
        age: voice.age || 'adult',
        accent: voice.accent || 'neutral',
      });
      
      if (voice.settings) {
        setVoiceSettings(voice.settings);
      }
    }
  }, [voice]);

  // Recording timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  if (!isOpen) return null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSettingsChange = (setting: keyof VoiceSettings, value: number | boolean) => {
    setVoiceSettings(prev => ({ ...prev, [setting]: value }));
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const audioFiles = files.filter(file => file.type.startsWith('audio/'));
    
    if (audioFiles.length !== files.length) {
      setError('Please upload only audio files');
      return;
    }

    if (audioSamples.length + audioFiles.length > 10) {
      setError('Maximum 10 audio samples allowed');
      return;
    }

    setAudioSamples(prev => [...prev, ...audioFiles]);
    setError(null);
  };

  const removeAudioSample = (index: number) => {
    setAudioSamples(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingSample = (sampleId: string) => {
    setExistingSamples(prev => prev.filter(sample => sample._id !== sampleId));
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks: BlobPart[] = [];

      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/wav' });
        const file = new File([blob], `recording-${Date.now()}.wav`, { type: 'audio/wav' });
        setAudioSamples(prev => [...prev, file]);
        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingTime(0);
    } catch (error) {
      setError('Failed to access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      setMediaRecorder(null);
      setIsRecording(false);
    }
  };

  const playAudio = (file: File | { url: string; _id: string }) => {
    const audio = new Audio();
    
    if (file instanceof File) {
      audio.src = URL.createObjectURL(file);
    } else {
      audio.src = file.url;
    }

    audio.onended = () => setPlayingAudio(null);
    audio.play();
    setPlayingAudio(file instanceof File ? file.name : file._id);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Voice name is required');
      return;
    }

    if (audioSamples.length === 0 && existingSamples.length === 0) {
      setError('At least one audio sample is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const updateData = new FormData();
      
      // Basic info
      updateData.append('name', formData.name);
      updateData.append('description', formData.description);
      updateData.append('language', formData.language);
      updateData.append('gender', formData.gender);
      updateData.append('age', formData.age);
      updateData.append('accent', formData.accent);

      // Voice settings
      updateData.append('settings', JSON.stringify(voiceSettings));

      // New audio samples
      audioSamples.forEach((file, index) => {
        updateData.append('audioSamples', file);
      });

      // Existing samples to keep
      updateData.append('existingSamples', JSON.stringify(existingSamples.map(s => s._id)));

      await voiceApi.updateVoice(voice._id, updateData);
      onVoiceUpdated();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update voice');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Voice Name *
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Enter voice name"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Describe this voice..."
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Language
          </label>
          <select
            name="language"
            value={formData.language}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="en">English</option>
            <option value="hi">Hindi</option>
            <option value="bn">Bengali</option>
            <option value="te">Telugu</option>
            <option value="mr">Marathi</option>
            <option value="ta">Tamil</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Gender
          </label>
          <select
            name="gender"
            value={formData.gender}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="neutral">Neutral</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Age Group
          </label>
          <select
            name="age"
            value={formData.age}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="child">Child</option>
            <option value="young_adult">Young Adult</option>
            <option value="adult">Adult</option>
            <option value="senior">Senior</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Accent
          </label>
          <input
            type="text"
            name="accent"
            value={formData.accent}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g., American, British, Indian"
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Audio Samples</h3>
        <p className="text-sm text-gray-600 mb-4">
          Update audio samples for voice cloning. You can add new samples or remove existing ones.
        </p>
      </div>

      {/* Existing Samples */}
      {existingSamples.length > 0 && (
        <div>
          <h4 className="text-md font-medium text-gray-800 mb-3">Existing Samples</h4>
          <div className="space-y-2">
            {existingSamples.map((sample) => (
              <div key={sample._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => playAudio(sample)}
                    className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg"
                  >
                    {playingAudio === sample._id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{sample.filename}</p>
                    <p className="text-xs text-gray-500">{(sample.size / 1024).toFixed(1)} KB</p>
                  </div>
                </div>
                <button
                  onClick={() => removeExistingSample(sample._id)}
                  className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload New Samples */}
      <div>
        <h4 className="text-md font-medium text-gray-800 mb-3">Add New Samples</h4>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
          <div className="text-center">
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <label className="cursor-pointer inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                <Upload className="w-4 h-4 mr-2" />
                Upload Files
                <input
                  type="file"
                  multiple
                  accept="audio/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
              <button
                onClick={isRecording ? stopRecording : startRecording}
                className={`inline-flex items-center px-4 py-2 rounded-lg ${
                  isRecording
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-gray-600 text-white hover:bg-gray-700'
                }`}
              >
                <Mic className="w-4 h-4 mr-2" />
                {isRecording ? `Stop (${formatTime(recordingTime)})` : 'Record'}
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Upload audio files or record directly. Maximum 10 samples.
            </p>
          </div>
        </div>
      </div>

      {/* New Audio Samples */}
      {audioSamples.length > 0 && (
        <div>
          <h4 className="text-md font-medium text-gray-800 mb-3">New Samples</h4>
          <div className="space-y-2">
            {audioSamples.map((file, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => playAudio(file)}
                    className="p-2 text-green-600 hover:bg-green-100 rounded-lg"
                  >
                    {playingAudio === file.name ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                </div>
                <button
                  onClick={() => removeAudioSample(index)}
                  className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Voice Settings</h3>
        <p className="text-sm text-gray-600 mb-6">
          Fine-tune the voice characteristics for optimal results.
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Stability: {voiceSettings.stability.toFixed(2)}
          </label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={voiceSettings.stability}
            onChange={(e) => handleSettingsChange('stability', parseFloat(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <p className="text-xs text-gray-500 mt-1">
            Higher values make the voice more stable but less expressive
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Similarity Boost: {voiceSettings.similarityBoost.toFixed(2)}
          </label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={voiceSettings.similarityBoost}
            onChange={(e) => handleSettingsChange('similarityBoost', parseFloat(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <p className="text-xs text-gray-500 mt-1">
            Enhances similarity to the original voice
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Style: {voiceSettings.style.toFixed(2)}
          </label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={voiceSettings.style}
            onChange={(e) => handleSettingsChange('style', parseFloat(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <p className="text-xs text-gray-500 mt-1">
            Controls the style and emotion of the voice
          </p>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="speakerBoost"
            checked={voiceSettings.speakerBoost}
            onChange={(e) => handleSettingsChange('speakerBoost', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="speakerBoost" className="ml-2 block text-sm text-gray-700">
            Speaker Boost
          </label>
          <p className="text-xs text-gray-500 ml-2">
            Enhances speaker characteristics
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Edit Voice</h2>
            <p className="text-sm text-gray-600 mt-1">Update your voice clone settings</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep >= step
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {step}
                </div>
                <span className={`ml-2 text-sm ${
                  currentStep >= step ? 'text-blue-600 font-medium' : 'text-gray-500'
                }`}>
                  {step === 1 ? 'Basic Info' : step === 2 ? 'Audio Samples' : 'Settings'}
                </span>
                {step < 3 && <div className="w-8 h-px bg-gray-300 ml-4" />}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <button
            onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
            disabled={currentStep === 1}
            className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            {currentStep < 3 ? (
              <button
                onClick={() => setCurrentStep(currentStep + 1)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Updating...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Update Voice
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditVoiceModal;
